import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Brush } from 'lucide-react'
import { useSupabaseClient } from 'src/api/supabase'
import { queryKeys } from 'src/api/db/queryKeys'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { PageContainer } from 'src/shared/components/PageContainer'
import {
  ToggleGroup,
  ToggleGroupItem,
} from 'src/shared/components/ui/toggle-group'

import { DecorationSetupCostTable } from '../Decorations/components/DecorationSetupCostTable'
import { DecorationUnitCostTable } from '../Decorations/components/DecorationUnitCostTable'
import { DecorationInfoAssociator } from './components/DecorationInfoAssociator'
import { OrphanDropdown } from './components/OrphanDropdown'
import { useOrphanPrintCodes } from './hooks'

const invalidateDecorations = (
  queryClient: ReturnType<typeof useQueryClient>
) => queryClient.invalidateQueries({ queryKey: [queryKeys.decorations] })

export const DecorationsCostsPage = () => {
  const queryClient = useQueryClient()
  const supabase = useSupabaseClient()

  const [vendor, setVendor] = useState<string | null>(null)
  const [printCode, setPrintCode] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<'missing' | 'complete'>('missing')

  const hasSelection = !!vendor && !!printCode

  const {
    missingFromInfo,
    missingFromUnitCost,
    missingFromSetupCost,
    hasInfo,
    hasUnitCost,
    hasSetupCost,
  } = useOrphanPrintCodes()

  const isMissing = viewMode === 'missing'

  const handleSelect = (v: string, pc: string) => {
    setVendor(v)
    setPrintCode(pc)
  }

  const { data: setupCosts, isFetching: isSetupCostsFetching } = useQuery({
    queryKey: [queryKeys.decorations, 'setupCost', vendor, printCode],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('decoration_setup_cost')
        .select('*')
        .eq('vendor', vendor!)
        .eq('print_code', printCode!)
        .order('id', { ascending: true })
      if (error) throw error
      return data ?? []
    },
    enabled: hasSelection,
  })

  const createSetupCost = useMutation({
    mutationFn: async (insert: Record<string, unknown>) => {
      const { data, error } = await supabase
        .from('decoration_setup_cost')
        .insert(insert)
        .select('id')
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => invalidateDecorations(queryClient),
  })

  const updateSetupCost = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: number
      updates: Record<string, unknown>
    }) => {
      const { error } = await supabase
        .from('decoration_setup_cost')
        .update(updates)
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => invalidateDecorations(queryClient),
  })

  const deleteSetupCost = useMutation({
    mutationFn: async (id: number) => {
      const { error } = await supabase
        .from('decoration_setup_cost')
        .delete()
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => invalidateDecorations(queryClient),
  })

  const { data: unitCosts, isFetching: isUnitCostsFetching } = useQuery({
    queryKey: [queryKeys.decorations, 'unitCost', vendor, printCode],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('decoration_unit_cost')
        .select('*')
        .eq('vendor', vendor!)
        .eq('print_code', printCode!)
        .order('id', { ascending: true })
      if (error) throw error
      return data ?? []
    },
    enabled: hasSelection,
  })

  const createUnitCost = useMutation({
    mutationFn: async (insert: Record<string, unknown>) => {
      const { data, error } = await supabase
        .from('decoration_unit_cost')
        .insert(insert)
        .select('id')
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => invalidateDecorations(queryClient),
  })

  const updateUnitCost = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: number
      updates: Record<string, unknown>
    }) => {
      const { error } = await supabase
        .from('decoration_unit_cost')
        .update(updates)
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => invalidateDecorations(queryClient),
  })

  const deleteUnitCost = useMutation({
    mutationFn: async (id: number) => {
      const { error } = await supabase
        .from('decoration_unit_cost')
        .delete()
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => invalidateDecorations(queryClient),
  })

  return (
    <PageContainer width="xl">
      <Column className="gap-6">
        <Row className="gap-4 items-center justify-between flex-wrap">
          <Row className="gap-2 items-center">
            <Brush className="h-6 w-6" />
            <h1>Decorations costs</h1>
          </Row>

          <ToggleGroup
            type="single"
            value={viewMode}
            onValueChange={(v) => {
              if (v) setViewMode(v as 'missing' | 'complete')
            }}
            variant="primary"
            size="sm"
          >
            <ToggleGroupItem value="missing">Missing</ToggleGroupItem>
            <ToggleGroupItem value="complete">Complete</ToggleGroupItem>
          </ToggleGroup>
        </Row>

        <Row className="gap-4 flex-wrap">
          <OrphanDropdown
            label={isMissing ? 'Missing from info' : 'Has info'}
            items={isMissing ? missingFromInfo : hasInfo}
            selectedVendor={vendor}
            selectedPrintCode={printCode}
            onSelect={handleSelect}
          />
          <OrphanDropdown
            label={isMissing ? 'Missing unit cost' : 'Has unit cost'}
            items={isMissing ? missingFromUnitCost : hasUnitCost}
            selectedVendor={vendor}
            selectedPrintCode={printCode}
            onSelect={handleSelect}
          />
          <OrphanDropdown
            label={isMissing ? 'Missing setup cost' : 'Has setup cost'}
            items={isMissing ? missingFromSetupCost : hasSetupCost}
            selectedVendor={vendor}
            selectedPrintCode={printCode}
            onSelect={handleSelect}
          />
        </Row>

        {!hasSelection && (
          <Column className="py-8 text-center text-muted-foreground text-sm">
            Select a vendor and print code to view and edit decoration costs
          </Column>
        )}

        {hasSelection && (
          <Column className="gap-2">
            <DecorationInfoAssociator printCode={printCode!} />

            <DecorationSetupCostTable
              data={setupCosts ?? []}
              vendor={vendor!}
              printCode={printCode!}
              isLoading={isSetupCostsFetching}
              onSave={async (updates) => {
                await Promise.all(
                  updates.map(({ id, ...rest }) =>
                    updateSetupCost.mutateAsync({ id, updates: rest })
                  )
                )
              }}
              onAdd={async (insert) => {
                await createSetupCost.mutateAsync(insert)
              }}
              onDelete={async (id) => {
                await deleteSetupCost.mutateAsync(id)
              }}
            />

            <DecorationUnitCostTable
              data={unitCosts ?? []}
              vendor={vendor!}
              printCode={printCode!}
              isLoading={isUnitCostsFetching}
              onSave={async (updates) => {
                await Promise.all(
                  updates.map(({ id, ...rest }) =>
                    updateUnitCost.mutateAsync({ id, updates: rest })
                  )
                )
              }}
              onAdd={async (insert) => {
                await createUnitCost.mutateAsync(insert)
              }}
              onDelete={async (id) => {
                await deleteUnitCost.mutateAsync(id)
              }}
            />
          </Column>
        )}
      </Column>
    </PageContainer>
  )
}
